import React, { useState, useEffect, useCallback } from 'react';
import './seeable.css';
import './App.css';
import DateInput from './components/DateInput';
import GenderInput from './components/GenderInput';
import FortuneResume from './components/FortuneResume';
import AnnualFortunePage from './components/AnnualFortunePage';
import CompatibilityPage from './components/CompatibilityPage';
import NamingPage from './components/NamingPage';
import DateSelectionPage from './components/DateSelectionPage';
import MemberPage from './components/MemberPage';
import ForumPage from './components/ForumPage';
import BlogPage, { blogLabels } from './components/BlogPage';
import forumI18n from './services/forumI18n';
import Chatbot from './components/Chatbot';
import { calculateBazi, getMember, logoutMember } from './services/api';
import { translate, getLang, listenLangChange } from './services/i18n';

const siteChrome = {
  'zh-TW': {
    brand: 'Seeable.Life',
    navHome: '首頁',
    navWts: '黃大仙靈籤',
    navChekong: '車公靈籤',
    navGuoyin: '觀音靈籤',
    navFortune: '靈籤問路',
    navBazi: '綜合命理',
    navCompatibility: '情侶配對',
    navNaming: '起運改名',
    navDateSelection: '擇日',
    navMysticTools: '玄學工具',
    navServices: '玄學服務',
    navBlog: '部落格',
    navPrivate: '私人風水顧問',
    terms: '使用條款',
    privacy: '私隱聲明',
    edit: '修改資料',
    birthLabel: '出生資料',
    male: '男',
    female: '女',
    footer: '© 2026 Seeable.Life'
  },
  'zh-CN': {
    brand: 'Seeable.Life',
    navHome: '首页',
    navWts: '黄大仙灵签',
    navChekong: '车公灵签',
    navGuoyin: '观音灵签',
    navFortune: '灵签问路',
    navBazi: '综合命理',
    navCompatibility: '情侣配对',
    navNaming: '起运改名',
    navDateSelection: '择日',
    navMysticTools: '玄学工具',
    navServices: '玄学服务',
    navBlog: '博客',
    navPrivate: '私人风水顾问',
    terms: '使用条款',
    privacy: '隐私声明',
    edit: '修改资料',
    birthLabel: '出生资料',
    male: '男',
    female: '女',
    footer: '© 2026 Seeable.Life'
  },
  'en': {
    brand: 'Seeable.Life',
    navHome: 'Home',
    navWts: 'Wong Tai Sin',
    navChekong: 'Che Kong',
    navGuoyin: 'Guo Yin',
    navFortune: 'Fortune Sticks',
    navBazi: 'Integrated Destiny',
    navCompatibility: 'Couple Match',
    navNaming: 'Destiny Naming',
    navDateSelection: 'Auspicious Dates',
    navMysticTools: 'Metaphysical Tools',
    navServices: 'Mystic Services',
    navBlog: 'Blog',
    navPrivate: 'Private Feng Shui Advisor',
    terms: 'Terms',
    privacy: 'Privacy',
    edit: 'Edit details',
    birthLabel: 'Birth details',
    male: 'Male',
    female: 'Female',
    footer: '© 2026 Seeable.Life'
  },
  'ko': {
    brand: 'Seeable.Life',
    navHome: '홈',
    navWts: '웡타이신',
    navChekong: '처공',
    navGuoyin: '관음',
    navFortune: '영험 제비',
    navBazi: '종합 명리',
    navCompatibility: '커플 궁합',
    navNaming: '개명 운세',
    navDateSelection: '택일',
    navMysticTools: '현학 도구',
    navServices: '현학 서비스',
    navBlog: 'Blog',
    navPrivate: '개인 풍수 고문',
    terms: '이용약관',
    privacy: '개인정보',
    edit: '정보 수정',
    birthLabel: '출생 정보',
    male: '남성',
    female: '여성',
    footer: '© 2026 Seeable.Life'
  },
  'ja': {
    brand: 'Seeable.Life',
    navHome: 'ホーム',
    navWts: '黄大仙',
    navChekong: '車公',
    navGuoyin: '観音',
    navFortune: '霊籤問路',
    navBazi: '総合命理',
    navCompatibility: '恋人相性',
    navNaming: '開運改名',
    navDateSelection: '擇日',
    navMysticTools: '玄学ツール',
    navServices: '玄学サービス',
    navBlog: 'Blog',
    navPrivate: '個人風水顧問',
    terms: '利用規約',
    privacy: 'プライバシー',
    edit: '入力を修正',
    birthLabel: '出生情報',
    male: '男性',
    female: '女性',
    footer: '© 2026 Seeable.Life'
  },
  'ms': {
    brand: 'Seeable.Life',
    navHome: 'Utama',
    navWts: 'Wong Tai Sin',
    navChekong: 'Che Kong',
    navGuoyin: 'Guo Yin',
    navFortune: 'Batang ramalan',
    navBazi: 'Metafizik Bersepadu',
    navCompatibility: 'Padanan pasangan',
    navNaming: 'Nama pembawa tuah',
    navDateSelection: 'Pilih hari baik',
    navMysticTools: 'Alat metafizik',
    navServices: 'Khidmat metafizik',
    navBlog: 'Blog',
    navPrivate: 'Penasihat Feng Shui Peribadi',
    terms: 'Terma',
    privacy: 'Privasi',
    edit: 'Edit butiran',
    birthLabel: 'Butiran lahir',
    male: 'Lelaki',
    female: 'Perempuan',
    footer: '© 2026 Seeable.Life'
  },
  'es': {
    brand: 'Seeable.Life',
    navHome: 'Inicio',
    navWts: 'Wong Tai Sin',
    navChekong: 'Che Kong',
    navGuoyin: 'Guo Yin',
    navFortune: 'Varillas del oráculo',
    navBazi: 'Destino integrado',
    navCompatibility: 'Compatibilidad',
    navNaming: 'Nombre propicio',
    navDateSelection: 'Fechas propicias',
    navMysticTools: 'Herramientas místicas',
    navServices: 'Servicios místicos',
    navBlog: 'Blog',
    navPrivate: 'Asesor Feng Shui privado',
    terms: 'Términos',
    privacy: 'Privacidad',
    edit: 'Editar datos',
    birthLabel: 'Datos de nacimiento',
    male: 'Hombre',
    female: 'Mujer',
    footer: '© 2026 Seeable.Life'
  }
};

const brandMarkPath = `${process.env.PUBLIC_URL || '/bazi'}/brand/seeable-mark.svg`;

const pageMeta = {
  'zh-TW': {
    title: '四柱八字排盤與綜合命理 | Seeable.Life',
    description: '免費線上八字排盤，合看四柱、鬼谷兩頭鉗、稱骨、流年、合婚、改名與擇日。'
  },
  'zh-CN': {
    title: '综合命理 | Seeable.Life',
    description: '三坛灵签之外，综合四柱、鬼谷、称骨与流年，一局照命。'
  },
  'en': {
    title: 'Bazi Calculator and Chinese Metaphysics Tools | Seeable.Life',
    description: 'Free Bazi calculator with Guiguzi, bone-weight destiny, annual fortune, compatibility, naming and auspicious date tools.'
  },
  'ko': {
    title: '종합 명리 | Seeable.Life',
    description: '사주 명반, 자평, 대운, 귀곡자 양두겸을 계산하고 질문에 따라 풀이합니다.'
  },
  'ja': {
    title: '総合命理 | Seeable.Life',
    description: '四柱命盤、子平、大運、鬼谷子両頭鉗を算出し、問いに応じて読み解きます。'
  },
  'ms': {
    title: 'Metafizik Bersepadu | Seeable.Life',
    description: 'Carta empat tiang, Zi Ping, kitaran nasib dan Guiguzi dikira dahulu, lalu ditafsir mengikut soalan.'
  },
  'es': {
    title: 'Destino integrado | Seeable.Life',
    description: 'Calcula los cuatro pilares, Zi Ping, ciclos de suerte y Guiguzi, luego interpreta según tu pregunta.'
  }
};

const compatibilityMeta = {
  'zh-TW': {
    title: '八字情侶配對與合婚 | Seeable.Life',
    description: '免費八字情侶配對工具，合看兩人四柱、五行喜忌、夫妻宮、沖合刑害、性格互動、姻緣與長久相處節奏。'
  },
  'zh-CN': {
    title: '情侣配对 | Seeable.Life',
    description: '两盘四柱相照，以干支配合、五行喜忌、夫妻宫与冲合刑害细看情侣是否相配。'
  },
  'en': {
    title: 'Bazi Compatibility and Couple Match | Seeable.Life',
    description: 'Compare two Bazi charts for temperament, five-element balance, romance, timing and long-term compatibility.'
  },
  'ko': {
    title: '커플 궁합 | Seeable.Life',
    description: '두 사람의 사주를 비교해 성격, 상호작용, 인연과 장기 궁합을 봅니다.'
  },
  'ja': {
    title: '恋人相性 | Seeable.Life',
    description: '二人の四柱命盤を照らし、性格、相互作用、婚縁、長期相性を読みます。'
  },
  'ms': {
    title: 'Padanan pasangan | Seeable.Life',
    description: 'Bandingkan dua carta Bazi untuk watak, interaksi, cinta dan keserasian jangka panjang.'
  },
  'es': {
    title: 'Compatibilidad de pareja | Seeable.Life',
    description: 'Compara dos cartas Bazi: carácter, interacción, amor y compatibilidad a largo plazo.'
  }
};

const namingMeta = {
  'zh-TW': {
    title: '起名改名與姓名吉凶分析 | Seeable.Life',
    description: '免費起運改名工具，以生辰四柱配三才五格、康熙筆劃、81數理、字義吉凶與生肖喜忌，分析中文姓名是否承運。'
  },
  'zh-CN': {
    title: '起运改名 | Seeable.Life',
    description: '以生辰四柱配三才五格、81数理、字义吉凶与生肖喜忌，细看姓名是否承运。'
  },
  'en': {
    title: 'Chinese Naming and Name Fortune Analysis | Seeable.Life',
    description: 'Analyze Chinese names by Bazi, San Cai Wu Ge, Kangxi strokes, 81-number fortune, meaning and zodiac-radical fit.'
  },
  'ko': {
    title: '개명 운세 | Seeable.Life',
    description: '사주와 삼재오격, 81수리, 이름 뜻과 띠의 자형 궁합으로 이름을 봅니다.'
  },
  'ja': {
    title: '開運改名 | Seeable.Life',
    description: '四柱と三才五格、81数理、字義、生肖の字形喜忌から姓名を読みます。'
  },
  'ms': {
    title: 'Nama pembawa tuah | Seeable.Life',
    description: 'Analisis nama Cina dengan Bazi, San Cai Wu Ge, nasib 81 nombor, makna nama dan kesesuaian zodiak.'
  },
  'es': {
    title: 'Nombre propicio | Seeable.Life',
    description: 'Analiza un nombre chino con Bazi, San Cai Wu Ge, fortuna de 81 números, significado y ajuste zodiacal.'
  }
};

const dateSelectionMeta = {
  'zh-TW': {
    title: '擇日與黃道吉日查詢 | Seeable.Life',
    description: '免費擇日工具，依欽定協紀辨方書取法，按日期範圍、用事與相關人士生辰，推選結婚、開張、搬屋等黃道吉日。'
  },
  'zh-CN': {
    title: '择日 | Seeable.Life',
    description: '依钦定协纪辨方书取法，按日期范围、用事与相关人士生辰，推选吉日。'
  },
  'en': {
    title: 'Auspicious Date Selection and Chinese Almanac | Seeable.Life',
    description: 'Choose auspicious wedding, opening, moving and event dates by date range, birth details and Chinese almanac rules.'
  },
  'ko': {
    title: '택일 | Seeable.Life',
    description: '행사, 날짜 범위, 관련 인물의 생년월일로 길일을 고릅니다.'
  },
  'ja': {
    title: '擇日 | Seeable.Life',
    description: '用事、期間、関係者の生年月日から吉日を選びます。'
  },
  'ms': {
    title: 'Pilih hari baik | Seeable.Life',
    description: 'Pilih hari baik mengikut acara, julat tarikh dan butiran lahir pihak berkaitan.'
  },
  'es': {
    title: 'Fechas propicias | Seeable.Life',
    description: 'Elige fechas propicias según evento, rango de fechas y nacimientos relacionados.'
  }
};

const annualMeta = {
  'zh-TW': {
    title: '流年運程與生肖運勢 | Seeable.Life',
    description: '輸入出生生辰，免費推算前一年、今年與下一個農曆年的整體、家庭、財富、婚姻、事業、健康與逐月流年運程。'
  },
  'zh-CN': {
    title: '流年运程 | Seeable.Life',
    description: '按出生生辰与生肖地支，推算前一年、今年与下一个农历年的整体、家庭、财富、婚姻、事业与健康运程。'
  },
  'en': {
    title: 'Annual Zodiac Fortune and Tai Sui Cycles | Seeable.Life',
    description: 'Calculate previous, current and next lunar-year fortune by birth details, zodiac branch, Tai Sui cycles and monthly luck.'
  },
  'ko': {
    title: '연간 운세 | Seeable.Life',
    description: '생년월일과 띠 지지를 바탕으로 전년, 올해, 다음 음력년의 가정, 재물, 사랑, 일, 건강 운세를 봅니다.'
  },
  'ja': {
    title: '流年運程 | Seeable.Life',
    description: '生年月日と生肖地支から、前年・今年・翌旧暦年の全体、家庭、財、恋愛、仕事、健康を読みます。'
  },
  'ms': {
    title: 'Nasib tahunan | Seeable.Life',
    description: 'Kira nasib tahun lunar lalu, semasa dan seterusnya mengikut butiran lahir, zodiak dan kitaran metafizik Cina.'
  },
  'es': {
    title: 'Fortuna anual | Seeable.Life',
    description: 'Calcula la fortuna del año lunar anterior, actual y siguiente según nacimiento, zodiaco y ciclos metafísicos chinos.'
  }
};

const SEO_ORIGIN = 'https://seeable.life';
const SEO_IMAGE = `${SEO_ORIGIN}/brand/seeable-og.svg`;
const ogLocaleByLang = {
  'zh-TW': 'zh_TW',
  'zh-CN': 'zh_CN',
  en: 'en_US',
  ko: 'ko_KR',
  ja: 'ja_JP',
  ms: 'ms_MY',
  es: 'es_ES'
};
const seoRouteConfig = {
  '/bazi': {
    path: '/bazi/',
    meta: pageMeta,
    schemaName: 'Seeable.Life 四柱八字排盤與綜合命理',
    schemaDescription: 'Free online Bazi, Guiguzi, bone-weight destiny, annual fortune, compatibility, naming and auspicious date tools.'
  },
  '/bazi/annual': {
    path: '/bazi/annual/',
    meta: annualMeta,
    schemaName: 'Seeable.Life 流年運程',
    schemaDescription: 'Annual lunar-year and Chinese zodiac fortune calculator with monthly guidance.'
  },
  '/bazi/compatibility': {
    path: '/bazi/compatibility/',
    meta: compatibilityMeta,
    schemaName: 'Seeable.Life 八字情侶配對',
    schemaDescription: 'Couple compatibility calculator based on two Bazi charts, five elements and relationship patterns.'
  },
  '/bazi/naming': {
    path: '/bazi/naming/',
    meta: namingMeta,
    schemaName: 'Seeable.Life 起名改名分析',
    schemaDescription: 'Chinese naming calculator using Bazi, San Cai Wu Ge, Kangxi strokes, 81-number fortune and zodiac-radical fit.'
  },
  '/bazi/date-selection': {
    path: '/bazi/date-selection/',
    meta: dateSelectionMeta,
    schemaName: 'Seeable.Life 擇日與黃道吉日',
    schemaDescription: 'Auspicious date selection tool based on Chinese almanac rules, event type and birth details.'
  }
};

const ensureHeadNode = (selector, createNode) => {
  let node = document.querySelector(selector);
  if (!node) {
    node = createNode();
    document.head.appendChild(node);
  }
  return node;
};

const pad = (n) => String(n).padStart(2, '0');

function App() {
  const normalizedPath = window.location.pathname.replace(/\/+$/, '');
  const isBlogPage = normalizedPath === '/bazi/blog' || normalizedPath.startsWith('/bazi/blog/');
  const isForumPage = normalizedPath === '/bazi/forum' || normalizedPath.startsWith('/bazi/forum/');
  const isAnnualPage = normalizedPath.endsWith('/bazi/annual');
  const isCompatibilityPage = normalizedPath.endsWith('/bazi/compatibility');
  const isNamingPage = normalizedPath.endsWith('/bazi/naming');
  const isDateSelectionPage = normalizedPath.endsWith('/bazi/date-selection');
  const isMemberPage = normalizedPath.endsWith('/bazi/member');
  const [lang, setLang] = useState(getLang());
  const [memberPreview, setMemberPreview] = useState(null);
  const [formData, setFormData] = useState({ year: '', month: '', day: '', hour: '', minute: '', is_male: true });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(false);
  const chrome = siteChrome[lang] || siteChrome['zh-TW'];

  useEffect(() => {
    listenLangChange((newLang) => setLang(newLang));
  }, []);

  useEffect(() => {
    if (!isAnnualPage || isCompatibilityPage || isNamingPage || isDateSelectionPage || isMemberPage || result) return;
    try {
      const storedForm = sessionStorage.getItem('seeable_bazi_form');
      const storedResult = sessionStorage.getItem('seeable_bazi_result');
      if (storedForm && storedResult) {
        setFormData(JSON.parse(storedForm));
        setResult(JSON.parse(storedResult));
      }
    } catch (err) {
      console.error(err);
    }
  }, [isAnnualPage, isCompatibilityPage, isNamingPage, isDateSelectionPage, isMemberPage, result]);

  useEffect(() => {
    if (isForumPage || isBlogPage) return;
    const routeKey = isDateSelectionPage
      ? '/bazi/date-selection'
      : isMemberPage
        ? '/bazi'
        : isNamingPage
          ? '/bazi/naming'
          : isCompatibilityPage
            ? '/bazi/compatibility'
            : isAnnualPage
              ? '/bazi/annual'
              : '/bazi';
    const routeConfig = seoRouteConfig[routeKey];
    const metaSource = routeConfig.meta;
    const meta = metaSource[lang] || metaSource['zh-TW'];
    const canonicalUrl = `${SEO_ORIGIN}${routeConfig.path}`;
    document.documentElement.lang = lang;
    document.title = meta.title;

    const selectors = [
      ['meta[name="description"]', meta.description],
      ['meta[name="robots"]', 'index,follow,max-image-preview:large'],
      ['meta[property="og:locale"]', ogLocaleByLang[lang] || 'zh_TW'],
      ['meta[property="og:title"]', meta.title],
      ['meta[property="og:description"]', meta.description],
      ['meta[property="og:url"]', canonicalUrl],
      ['meta[property="og:image"]', SEO_IMAGE],
      ['meta[name="twitter:title"]', meta.title],
      ['meta[name="twitter:description"]', meta.description],
      ['meta[name="twitter:image"]', SEO_IMAGE]
    ];

    selectors.forEach(([selector, value]) => {
      const node = ensureHeadNode(selector, () => {
        const created = document.createElement('meta');
        const match = selector.match(/meta\[(name|property)="([^"]+)"\]/);
        if (match) created.setAttribute(match[1], match[2]);
        return created;
      });
      node.setAttribute('content', value);
    });

    const canonical = ensureHeadNode('link[rel="canonical"]', () => {
      const created = document.createElement('link');
      created.setAttribute('rel', 'canonical');
      return created;
    });
    canonical.setAttribute('href', canonicalUrl);

    const schema = ensureHeadNode('script#bazi-seo-schema', () => {
      const created = document.createElement('script');
      created.id = 'bazi-seo-schema';
      created.type = 'application/ld+json';
      return created;
    });
    schema.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'WebApplication',
      name: routeConfig.schemaName,
      url: canonicalUrl,
      applicationCategory: 'LifestyleApplication',
      operatingSystem: 'Web',
      isAccessibleForFree: true,
      inLanguage: ['zh-Hant', 'zh-Hans', 'en', 'ko', 'ja', 'ms', 'es'],
      description: routeConfig.schemaDescription,
      publisher: {
        '@type': 'Organization',
        name: 'Seeable.Life',
        url: SEO_ORIGIN,
        logo: `${SEO_ORIGIN}/brand/seeable-mark.svg`
      }
    });
  }, [lang, isAnnualPage, isCompatibilityPage, isNamingPage, isDateSelectionPage, isMemberPage, isForumPage, isBlogPage]);

  useEffect(() => {
    getMember()
      .then((data) => {
        if (data?.authenticated) setMemberPreview(data.member);
      })
      .catch(() => {});
  }, []);

  const T = useCallback((key) => translate(key, lang), [lang]);

  const handleDateChange = (dateData) => setFormData({ ...formData, ...dateData });
  const handleGenderChange = (isMale) => setFormData({ ...formData, is_male: isMale });

  const handleLangChange = (event) => {
    const nextLang = event.target.value;
    try {
      localStorage.setItem('fc_lang', nextLang);
      localStorage.setItem('wts_language', nextLang);
      localStorage.setItem('guoyin_language', nextLang);
    } catch (err) {
      console.error(err);
    }
    setLang(nextLang);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.year || !formData.month || !formData.day || formData.hour === '' || formData.minute === '') {
      setError(T('validationError'));
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const computed = await calculateBazi({ ...formData, lang });
      setResult(computed);
      try {
        sessionStorage.setItem('seeable_bazi_form', JSON.stringify(formData));
        sessionStorage.setItem('seeable_bazi_result', JSON.stringify(computed));
      } catch (storageErr) {
        console.error(storageErr);
      }
      setEditing(false);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setError(T('calcError'));
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleMemberLogout = async () => {
    await logoutMember();
    setMemberPreview(null);
    window.location.href = '/private-advisory.html?logout=success';
  };

  const handleReset = () => {
    setFormData({ year: '', month: '', day: '', hour: '', minute: '', is_male: true });
    setResult(null);
    setError(null);
    setEditing(false);
    try {
      sessionStorage.removeItem('seeable_bazi_form');
      sessionStorage.removeItem('seeable_bazi_result');
    } catch (err) {
      console.error(err);
    }
  };

  const birthSummary = formData.year
    ? `${formData.year}-${pad(formData.month)}-${pad(formData.day)} ${pad(formData.hour)}:${pad(formData.minute)}`
    : '';

  const theForm = (
    <form onSubmit={handleSubmit} className="bz-form">
      <DateInput value={formData} onChange={handleDateChange} t={T} />
      <GenderInput value={formData.is_male} onChange={handleGenderChange} t={T} />
      {error && <div className="fc-error" role="alert">{error}</div>}
      <div className="bz-form__actions">
        <button type="submit" className="fc-btn fc-btn--primary fc-btn--lg" disabled={loading}>
          {loading ? T('calculating') : result ? T('recalculate') : T('calculate')}
        </button>
        <button type="button" className="fc-btn fc-btn--ghost" onClick={handleReset}>{T('reset')}</button>
      </div>
    </form>
  );

  return (
    <div className="bz-app">
      <header className="fc-header">
        <div className="fc-header__inner">
          <a className="fc-brand" href="/">
            <img className="fc-brand__mark" src={brandMarkPath} alt="" />
            <span className="fc-brand__name">{chrome.brand}</span>
          </a>
          <nav className="fc-nav" aria-label="Primary">
            <a href="/">{chrome.navHome}</a>
            <a href="/guoyin/">{chrome.navFortune}</a>
            <a href="/bazi/" className={!isAnnualPage && !isCompatibilityPage && !isNamingPage && !isDateSelectionPage && !isMemberPage && !isForumPage && !isBlogPage ? 'active' : ''} aria-current={!isAnnualPage && !isCompatibilityPage && !isNamingPage && !isDateSelectionPage && !isMemberPage && !isForumPage && !isBlogPage ? 'page' : undefined}>{chrome.navBazi}</a>
            <a href="/bazi/compatibility/" className={isCompatibilityPage ? 'active' : ''} aria-current={isCompatibilityPage ? 'page' : undefined}>{chrome.navCompatibility}</a>
            <a href="/bazi/naming/" className={isNamingPage ? 'active' : ''} aria-current={isNamingPage ? 'page' : undefined}>{chrome.navNaming}</a>
            <a href="/bazi/date-selection/" className={isDateSelectionPage ? 'active' : ''} aria-current={isDateSelectionPage ? 'page' : undefined}>{chrome.navDateSelection}</a>
            <a href="/#mystic-tools">{chrome.navMysticTools}</a>
            <a href="/services.html">{chrome.navServices}</a>
            <a href="/bazi/blog/" className={isBlogPage ? 'active' : undefined} aria-current={isBlogPage ? 'page' : undefined}>{blogLabels[lang].name}</a>
            <a href="/bazi/forum/">{forumI18n(lang)[0]}</a>
            <a href="/private-advisory.html">{chrome.navPrivate}</a>
          </nav>
          {memberPreview && (
            <div className="member-chip">
              <a href="/bazi/member/" aria-label={chrome.navPrivate}>
                {memberPreview.picture ? <img src={memberPreview.picture} alt="" /> : <span>{String(memberPreview.name || 'S').slice(0, 1)}</span>}
              </a>
              <button type="button" className="fc-btn fc-btn--ghost fc-btn--sm" onClick={handleMemberLogout}>{blogLabels[lang].logout}</button>
            </div>
          )}
          <select className="fc-select bz-lang" value={lang} onChange={handleLangChange} aria-label="Language">
            <option value="zh-TW">繁體中文</option>
            <option value="zh-CN">简体中文</option>
            <option value="en">English</option>
            <option value="ko">한국어</option>
            <option value="ja">日本語</option>
            <option value="ms">Bahasa Malaysia</option>
            <option value="es">Español</option>
          </select>
        </div>
      </header>

      <main className="bz-main">
        {/* ---------- Entry: a single focused form ---------- */}
        {isCompatibilityPage && <CompatibilityPage t={T} lang={lang} />}
        {isNamingPage && <NamingPage t={T} lang={lang} />}
        {isDateSelectionPage && <DateSelectionPage t={T} lang={lang} />}
        {isMemberPage && <MemberPage lang={lang} />}
        {isForumPage && <ForumPage lang={lang} />}
        {isBlogPage && <BlogPage lang={lang} />}

        {!isCompatibilityPage && !isNamingPage && !isDateSelectionPage && !isMemberPage && !isForumPage && !isBlogPage && !result && (
          <section className="bz-intro fc-rise">
            <h1 className="bz-intro__title">{isAnnualPage ? T('annualPageTitle') : T('headerTitle')}</h1>
            <p className="bz-intro__desc">{isAnnualPage ? T('annualPageDesc') : T('headerDesc')}</p>
            <div className="bz-form-card">{theForm}</div>
          </section>
        )}

        {/* ---------- Result: form collapses into an editable summary ---------- */}
        {!isCompatibilityPage && !isNamingPage && !isDateSelectionPage && !isMemberPage && !isForumPage && !isBlogPage && result && (
          <section className="bz-result">
            <div className="bz-recap">
              <div className="bz-recap__info">
                <span className="fc-eyebrow">{chrome.birthLabel}</span>
                <span className="bz-recap__value fc-num">
                  {birthSummary}
                  <span className="bz-recap__sep">·</span>
                  {formData.is_male ? chrome.male : chrome.female}
                </span>
              </div>
              <button
                type="button"
                className="fc-btn fc-btn--secondary fc-btn--sm"
                onClick={() => setEditing((v) => !v)}
                aria-expanded={editing}
              >
                {editing ? T('hideForm') : chrome.edit}
              </button>
            </div>

            {editing && <div className="bz-form-card bz-form-card--inline">{theForm}</div>}

            {isAnnualPage
              ? <AnnualFortunePage data={result} t={T} lang={lang} />
              : <FortuneResume data={result} t={T} lang={lang} />}

            <Chatbot baziContext={result} t={T} lang={lang} />
          </section>
        )}
      </main>

      <footer className="fc-footer">
        <div className="fc-footer__inner">
          <p>{chrome.footer}</p>
          <nav className="fc-footer__links">
            <a href="/">{chrome.navHome}</a>
            <a href="/wts/">{chrome.navWts}</a>
            <a href="/services.html">{chrome.navServices}</a>
            <a href="/bazi/blog/" className={isBlogPage ? 'active' : undefined} aria-current={isBlogPage ? 'page' : undefined}>{blogLabels[lang].name}</a>
            <a href="/bazi/forum/">{forumI18n(lang)[0]}</a>
            <a href="/private-advisory.html">{chrome.navPrivate}</a>
            <a href="/terms.html">{chrome.terms}</a>
            <a href="/privacy.html">{chrome.privacy}</a>
          </nav>
        </div>
      </footer>
    </div>
  );
}

export default App;
